import Screen from "@/components/layout/Screen";
import FlexParkCard from "@/components/shared/FlexParkCard";
import FlexParkModal from "@/components/shared/FlexParkModal";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import ShowAddressModal from "@/components/shared/ShowAddressModal";
import { SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useAccount } from "@/hooks/useAccount";
import { useCustomTheme } from "@/hooks/useCustomTheme";
import { usePurchaseFlexPark } from "@/hooks/usePurchaseFlexPark";
import { saveToStorage } from "@/utils/secureStore";
import { T, useT } from "@transifex/react";
import { useEffect, useState } from "react";
import { Pressable } from "react-native";
import { v4 as uuidv4 } from "uuid";

export default function PurchaseFlexPark() {
  const [isAddressModalVisible, setIsAddressModalVisible] = useState<boolean>(false);
  const { user, passes, isFetching, isModalOpen, selectedRateProductID, handlePurchase, setIsModalOpen, processPurchase } = usePurchaseFlexPark();
  const { account, isFetching: isAccountFetching } = useAccount();
  const { skin } = useCustomTheme();
  const t = useT();

  useEffect(() => {
    if (account?.phone) saveToStorage("contact", account?.phone);
  }, [account]);

  const isLoading = isFetching || isAccountFetching;

  return (
    <Screen>
      {isFetching || isAccountFetching ? <SkeletonText className="h-[24px] w-[220px] mx-auto" /> : <PageTitle title={t("Select a FlexPark to Purchase")} />}

      {isLoading ? (
        <VStack className="gap-[8px]">
          <SkeletonText className="h-[20px] w-[250px] mx-auto" />
          <SkeletonText className="h-[20px] w-[180px] mx-auto" />
        </VStack>
      ) : (
        <Text className="font-[400] leading-[20px] text-[14px] text-center text-typography-900">
          <T _str="These FlexPark passes can only be redeemed at" />{" "}
          {skin.locationName ? (
            <Text className="font-[600] leading-[20px] text-[14px] text-center text-typography-900">
              {`${skin.locationName}.`}{" "}
              {skin.locationName && skin.locationAddress && (
                <Pressable className="inline" onPress={() => setIsAddressModalVisible(true)}>
                  <Text className="font-[400] leading-[20px] text-[14px] text-center text-typography-900 underline">
                    <T _str="Learn More." />
                  </Text>
                </Pressable>
              )}
            </Text>
          ) : (
            <Text className="font-[400] leading-[20px] text-[14px] text-center text-typography-900">{t("this location")}.</Text>
          )}
        </Text>
      )}

      {isLoading ? (
        <>
          <FlexParkCard key={`skeleton-${uuidv4()}`} title="" price="" data={[]} showEventRestrictionWarning={false} isLoading={true} />
          <FlexParkCard key={`skeleton-${uuidv4()}`} title="" price="" data={[]} showEventRestrictionWarning={false} isLoading={true} />
          <FlexParkCard key={`skeleton-${uuidv4()}`} title="" price="" data={[]} showEventRestrictionWarning={false} isLoading={true} />
        </>
      ) : (
        passes?.map((pass, index) => (
          <FlexParkCard
            key={pass.rateProductID}
            title={pass.passName}
            price={`$${pass.amount.toFixed(2)}`}
            data={pass.passNotes.map((note, i) => ({ id: `${index}-${i}`, text: note }))}
            onPurchase={() => handlePurchase(pass.rateProductID)}
            showEventRestrictionWarning={pass.showNotRedeemableWarning}
            isLoading={false}
          />
        ))
      )}

      <FlexParkModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        handleConfirm={() => selectedRateProductID && processPurchase(selectedRateProductID)}
        modalType={user ? "notice" : "needtologin"}
      />

      <ShowAddressModal
        isOpen={isAddressModalVisible}
        onClose={() => setIsAddressModalVisible(false)}
        locationName={skin.locationName}
        locationAddress={skin.locationAddress?.address}
      />
    </Screen>
  );
}
